import React from 'react'
import { useTranslation } from 'react-i18next'

const formatNumber = value => new Intl.NumberFormat('es-VE', {
  maximumFractionDigits: 2
}).format(Number(value) || 0)

const getChange = (current, previous) => {
  const currentValue = Number(current) || 0
  const previousValue = Number(previous) || 0
  if (previousValue === 0) return null
  return ((currentValue - previousValue) / previousValue) * 100
}

const KpiCard = ({ icon, tone, label, value, previousValue, helper }) => {
  const { t } = useTranslation()
  const change = previousValue === undefined ? undefined : getChange(value, previousValue)
  const changeClass = change > 0 ? 'is-positive' : change < 0 ? 'is-negative' : 'is-neutral'

  return (
    <article className={`dashboard-kpi-card dashboard-kpi-card--${tone}`}>
      <div className="dashboard-kpi-card__top">
        <span className="dashboard-kpi-card__icon" aria-hidden="true">
          <i className={icon} />
        </span>
        {change !== undefined && (
          <span className={`dashboard-kpi-card__trend ${changeClass}`}>
            {change === null ? (
              t('dashboard_no_previous_activity', { defaultValue: 'Sin actividad el mes anterior' })
            ) : (
              <>
                <i className={`fas ${change >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`} aria-hidden="true" />
                {' '}{Math.abs(change).toFixed(0)}%
              </>
            )}
          </span>
        )}
      </div>
      <div className="dashboard-kpi-card__value">
        {formatNumber(value)}
      </div>
      <h2 className="dashboard-kpi-card__label">{label}</h2>
      {helper && <p className="dashboard-kpi-card__helper">{helper}</p>}
    </article>
  )
}

const DashboardKpis = ({ data }) => {
  const { t } = useTranslation()
  const totals = data?.TotalsCounter || {}
  const current = data?.CurrentMonth || {}
  const previous = data?.PreviousMonth || {}
  const retainedCurrent = (Number(current.IvaRetainedTotal) || 0) + (Number(current.IslrRetainedTotal) || 0)
  const retainedPrevious = (Number(previous.IvaRetainedTotal) || 0) + (Number(previous.IslrRetainedTotal) || 0)

  return (
    <section className="dashboard-kpi-grid" aria-label={t('dashboard_summary', { defaultValue: 'Resumen del mes' })}>
      <KpiCard
        icon="fas fa-file-invoice"
        tone="blue"
        label={t('dashboard_invoices_month', { defaultValue: 'Facturas del mes' })}
        value={current.InvoiceCount ?? totals.InvoiceCountMonth}
        previousValue={previous.InvoiceCount}
        helper={`${formatNumber(totals.InvoiceCountTotal)} ${t('dashboard_historical', { defaultValue: 'históricas' })}`}
      />
      <KpiCard
        icon="fas fa-file-signature"
        tone="cyan"
        label={t('dashboard_withholdings_month', { defaultValue: 'Retenciones del mes' })}
        value={current.WithholdingCount ?? totals.WithholdingsCountMonth}
        previousValue={previous.WithholdingCount}
        helper={`${formatNumber(totals.WithholdingsCountTotal)} ${t('dashboard_historical', { defaultValue: 'históricas' })}`}
      />
      <KpiCard
        icon="fas fa-shopping-basket"
        tone="indigo"
        label={t('dashboard_purchases_month', { defaultValue: 'Compras registradas' })}
        value={current.PurchaseTotal}
        previousValue={previous.PurchaseTotal}
        helper={t('dashboard_issued_invoices', { defaultValue: 'Según fecha de emisión' })}
      />
      <KpiCard
        icon="fas fa-hand-holding-usd"
        tone="green"
        label={t('dashboard_taxes_retained', { defaultValue: 'Tributos retenidos' })}
        value={retainedCurrent}
        previousValue={retainedPrevious}
        helper={t('dashboard_iva_islr_total', { defaultValue: 'IVA + ISLR del mes' })}
      />
      <KpiCard
        icon="fas fa-users"
        tone="violet"
        label={t('dashboard_active_providers', { defaultValue: 'Proveedores activos' })}
        value={totals.ProvidersCount}
        helper={t('dashboard_provider_directory', { defaultValue: 'Directorio de esta empresa' })}
      />
      <KpiCard
        icon="fas fa-exclamation-circle"
        tone="amber"
        label={t('dashboard_pending_withholding', { defaultValue: 'Pendientes de retención' })}
        value={data?.PendingWithholdingInvoices}
        helper={t('dashboard_requires_action', { defaultValue: 'Facturas que requieren revisión' })}
      />
    </section>
  )
}

export default DashboardKpis
